<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDynamicHeight } from '@/composables/useDynamicHeight';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { type Shift, type ShiftWithoutFinancialInformation } from '@/api/shifts';
import { clinicalAuthorName } from '@/utils/clinical-author';
import { useShowVoided } from '@/composables/useShowVoided';
import ClinicalRecordHistory from '@/components/clinical-records/clinical-record-history.vue';
import ClinicalRecordVoid from '@/components/clinical-records/clinical-record-void.vue';
import ClinicalVoidMark from '@/components/clinical-records/clinical-void-mark.vue';
import ShowVoidedSwitch from '@/components/clinical-records/show-voided-switch.vue';
import ShiftReportTextsTimeline from './shift-report-texts-timeline.vue';

const { t } = useI18n();

const props = defineProps<{ shift: Shift | ShiftWithoutFinancialInformation }>();

// The shift's data comes from its parent, which reloads it after an anulación.
const emit = defineEmits<{ voided: [] }>();

const { showVoided, voidedCount, visibleItems } = useShowVoided(() => props.shift?.reportTexts);

const showTimelineDialog = ref(false);
const { dynamicHeight } = useDynamicHeight();
const { tableProps } = useResponsiveTable();

const headers = [
  { title: 'Fecha', value: 'humanCreatedAt' },
  { title: 'Registrado por', value: 'author' },
  { title: 'Texto', value: 'text' },
  { title: 'Ver Completo', value: 'formattedText' },
];
</script>
<template>
  <div>
    <v-data-table
      v-bind="tableProps"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :items="visibleItems"
      :height="dynamicHeight"
      item-key="id"
      class="pa-2"
      :no-data-text="t('shiftShowReportTexts.noReportTexts')"
      fixed-header
    >
      <template #top>
        <div class="justify-end align-center d-flex ga-2 pa-2">
          <show-voided-switch
            v-model="showVoided"
            :count="voidedCount"
          />
          <v-tooltip
            v-if="props?.shift?.reportTexts?.length > 0"
            :text="t('shiftShowReportTexts.timelineTooltip')"
            :aria-label="t('shiftShowReportTexts.timelineTooltip')"
            location="bottom"
          >
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                icon="mdi-timeline-clock-outline"
                color="primary"
                variant="outlined"
                size="small"
                @click="showTimelineDialog = true"
              />
            </template>
          </v-tooltip>
        </div>
      </template>
      <template #[`item.humanCreatedAt`]="{ item }">
        <span class="text-no-wrap">{{ item.humanCreatedAt }}</span>
        <clinical-record-history
          item-type="ReportText"
          :item-id="item.id"
          :edited="item.edited"
          :label="`Registro escrito · ${item.humanCreatedAt}`"
        />
        <clinical-record-void
          item-type="ReportText"
          :item-id="item.id"
          :can-void="item.canVoid"
          :label="`Registro escrito · ${item.humanCreatedAt}`"
          correction="text"
          :current-text="item.text"
          @voided="emit('voided')"
        />
      </template>
      <template #[`item.author`]="{ item }">
        {{ clinicalAuthorName(item.author) || '-' }}
      </template>
      <template #[`item.text`]="{ item }">
        <span
          class="text-truncate d-inline-block"
          :class="{ 'text-decoration-line-through text-medium-emphasis': item.voided }"
          style="max-width: 200px;"
        >
          {{ item.text }}
        </span>
        <clinical-void-mark
          :voided="item.voided"
          :details="item.voidDetails"
        />
      </template>
      <template #[`item.formattedText`]="{ item }">
        <v-dialog max-width="600">
          <template #activator="{ props: activatorProps }">
            <v-btn
              v-if="item.formattedText?.length > 0"
              v-bind="activatorProps"
              icon="mdi-eye"
              size="small"
              class="me-2"
            />
          </template>
          <template #default>
            <v-card>
              <v-card-text class="pre-wrap">
                {{ item.formattedText }}
              </v-card-text>
            </v-card>
          </template>
        </v-dialog>
      </template>
    </v-data-table>
    <shift-report-texts-timeline
      v-model="showTimelineDialog"
      :shift="props.shift"
    />
  </div>
</template>
<style scoped>
.pre-wrap {
  white-space: pre-wrap;
  overflow-wrap: break-word;
}
</style>
